import React, { useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check, ChevronRight, GitCompare, Mail, Plane, Send, Sparkles, Truck } from "lucide-react";
import { Link } from "react-router-dom";

import Accordion from "./components/Accordion";
import Button from "./components/Button";
import CalendlyInline from "./components/CalendlyInline";
import Card from "./components/Card";
import Chip from "./components/Chip";
import Container from "./components/Container";
import FeatureGrid from "./components/FeatureGrid";
import HeroAmbient from "./components/HeroAmbient";
import AmbientBackdrop from "../motionKit/AmbientBackdrop";
import FkAmbientCss from "../motionKit/FkAmbientCss";
import AnimatedLoader from "../motionKit/AnimatedLoader";
import MomentumScroll from "./components/MomentumScroll";
import Reveal from "./components/Reveal";
import Section from "./components/Section";
import ShinyText from "./components/ShinyText";
import { scrollToSection } from "./utils/scrollToSection";
import Footer from "../components/Footer";

const CALENDLY_URL = "https://calendly.com/shawak-vantagecybertech/30min";

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <div className="text-sm font-semibold text-fk-ink dark:text-gray-100">{label}</div>
      <div className="mt-2">{children}</div>
      {hint ? <div className="mt-2 text-xs text-fk-muted dark:text-gray-300">{hint}</div> : null}
    </label>
  );
}

export default function FreightKitLanding() {
  const reduceMotion = useReducedMotion();
  const [tourStep, setTourStep] = useState(0);

  const tourSteps = useMemo(
    () => [
      {
        id: "inbox",
        title: "Identify quote enquiries from your inbox",
        bullets: [
          "Connect FreightKit to your inbox so quoting starts where requests arrive.",
          "Identify inbound quote enquiries and capture key details into a workflow.",
        ],
      },
      {
        id: "profiles",
        title: "Use carrier profiles + templates to request rates",
        bullets: [
          "Store profiles for airlines, truckers, and transport providers.",
          "Send pre-filled rate requests using templates from the enquiry details.",
        ],
      },
      {
        id: "responses",
        title: "Capture responses and compare routes/prices",
        bullets: [
          "Store provider responses against the enquiry and keep them searchable.",
          "Compare offers by route, transit, and price without manual copy/paste.",
        ],
      },
      {
        id: "output",
        title: "Build the customer quote and send consistently",
        bullets: [
          "Apply your margin controls, charge library and templates.",
          "Send a clean, consistent quote format every time.",
        ],
      },
    ],
    []
  );

  const featureGroups = useMemo(
    () => [
      {
        title: "Quoting engine",
        subtitle: "Pricing logic that’s usable day-to-day.",
        items: [
          {
            label: "Lane templates",
            desc: "Repeatable lane quoting.",
          },
          {
            label: "Charge library",
            desc: "Standard charge codes.",
          },
          {
            label: "Margin presets & floors",
            desc: "Guardrails for consistency.",
          },
        ],
      },
      {
        title: "Workflow & management",
        subtitle: "Light CRM/ops helpers that keep quoting moving.",
        items: [
          {
            label: "Inbox connection + query identification",
            desc: "Turn inbound emails into quote work.",
          },
          {
            label: "Transport provider profiles",
            desc: "Airlines, truckers, and carriers kept organised.",
          },
          {
            label: "Rate request templates",
            desc: "Pre-filled emails built from the enquiry details.",
          },
          {
            label: "Response capture + comparison",
            desc: "Store replies and compare routes/prices.",
          },
          {
            label: "Quote history + search",
            desc: "Find and reuse prior quotes.",
          },
        ],
      },
      {
        title: "Consistency & control",
        subtitle: "Reduce pricing drift across people and time.",
        items: [
          {
            label: "Approved templates",
            desc: "Keep quoting consistent.",
          },
          {
            label: "Standardised output",
            desc: "Clean, repeatable breakdown.",
          },
          {
            label: "Operator oversight",
            desc: "Review, intervene, and adjust templates before sending.",
          },
          {
            label: "Approvals (optional)",
            desc: "Add checks where needed.",
            tag: "optional",
          },
        ],
      },
    ],
    []
  );

  const faqItems = useMemo(
    () => [
      {
        q: "Do I need to migrate all rates at once?",
        a: "No. Most teams start with a handful of high-volume lanes and a core charge library, then expand as templates stabilise. You can run FreightKit alongside existing spreadsheets during the transition.",
      },
      {
        q: "Can I handle customer-specific pricing?",
        a: "Yes. You can apply customer-specific defaults and overrides (for example, margin logic or recurring charges) so quotes remain consistent without manual rework.",
      },
      {
        q: "Can multiple team members quote?",
        a: "Yes. FreightKit is designed for shared workflows: templates, charge libraries, and standard outputs help sales and ops stay aligned. Permissions and approvals can be configured depending on how your team works.",
      },
      {
        q: "Do you support air/sea/road?",
        a: "FreightKit is built around lanes, charges, and surcharges, so it can fit air, sea, and road workflows. If you have service-specific edge cases, those are typically handled via templates and charge codes.",
      },
      {
        q: "Can I export quotes to PDF/email?",
        a: "Yes. You can generate a customer-ready quote output that stays consistent across the team. The exact layout is configurable so your brand and terms are standardised.",
      },
      {
        q: "What about VAT and incoterms?",
        a: "You can include VAT treatment and incoterms in your standard quote terms and templates. If you need deeper automation (for example, rule-based incoterms defaults by lane/customer), that can be added as part of rollout.",
      },
    ],
    []
  );

  return (
    <>
      {/* Page background lives OUTSIDE MomentumScroll's transform. MomentumScroll
          sets position:fixed + transform on its scroller, which makes any `fixed`
          descendant fill the full scroll height — that stretched the freight
          routes into vertical streaks. Pinned to the viewport here, they stay
          proportioned; the scrolling content sits transparent on top. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-fk-surface via-fk-bg to-fk-bg" />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-gray-950 to-gray-950 opacity-0 transition-opacity duration-1000 ease-out motion-reduce:transition-none dark:opacity-100" />
        {/* Remotion aurora — soft orbs drifting behind the page. */}
        <AmbientBackdrop className="absolute" intensity={1.25} />
        {/* Same always-on freight wallpaper as the authed app (routes + drifting
            planes/trucks/boxes). */}
        <FkAmbientCss className="absolute" intensity={0.85} />
      </div>
    <MomentumScroll>
      <div className="fk-theme-smooth relative z-10 overflow-hidden text-fk-ink dark:text-gray-100">
        <HeroAmbient />

        {/* HERO */}
        <section className="relative pt-12 sm:pt-16">
          <Container>
            <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-12">
              <Reveal>
                <div className="flex flex-wrap items-center gap-3">
                  <Chip>Quoting-first</Chip>
                  <Chip>Built for freight forwarders</Chip>
                  <Chip>Inbox-connected</Chip>
                </div>

                <h1 className="mt-6 text-balance text-4xl font-semibold tracking-tight text-fk-ink dark:text-gray-100 sm:text-5xl lg:text-6xl">
                  Instant <ShinyText>freight quotes</ShinyText>. Consistent margins.
                </h1>

                <p className="mt-5 max-w-xl text-pretty text-base leading-relaxed text-fk-muted dark:text-gray-300 sm:text-lg sm:leading-relaxed">
                  Connect to your inbox, identify inbound quote enquiries, then request carrier rates with
                  templates, capture responses, compare routes/prices, and send a clean customer quote. You stay in
                  control: review, intervene, and adjust templates before anything goes out.
                </p>

                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <Button
                    as="a"
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noreferrer"
                    variant="accent"
                    withArrow
                    className="rounded-2xl px-6 py-3.5"
                  >
                    Book a demo
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => scrollToSection("tour")}
                    withArrow
                    className="rounded-2xl px-6 py-3.5"
                  >
                    See how it works
                  </Button>
                </div>

                <div className="mt-8 flex flex-wrap gap-6 text-sm text-fk-muted dark:text-gray-300">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-fk-accent" />
                    Inbox-connected workflow
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-fk-accent" />
                    Provider profiles + comparisons
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.08}>
                <Card className="relative overflow-hidden p-6" hoverable={false}>
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(59,130,246,0.22),transparent_55%),radial-gradient(circle_at_80%_30%,rgba(96,165,250,0.20),transparent_55%),radial-gradient(circle_at_50%_90%,rgba(11,18,32,0.10),transparent_60%)]"
                  />
                  <div className="relative">
                    <div className="text-sm font-semibold text-fk-ink dark:text-gray-100">Inbox → Rates → Quote</div>
                    <div className="mt-2 text-sm text-fk-muted dark:text-gray-300">
                      Identify enquiries, request rates, capture replies, compare options, send the quote.
                    </div>

                    <div className="mt-6 grid gap-3 sm:grid-cols-2">
                      {[
                        { icon: Sparkles, title: "Identify", desc: "Detect quote enquiries" },
                        { icon: Plane, title: "Profiles", desc: "Airlines + truckers" },
                        { icon: Truck, title: "Request", desc: "Template emails" },
                        { icon: GitCompare, title: "Compare", desc: "Routes + prices" },
                      ].map((x) => {
                        const Icon = x.icon;
                        return (
                          <div
                            key={x.title}
                            className="flex items-start gap-3 rounded-2xl border border-fk-border bg-fk-surface/75 p-4 dark:border-white/10 dark:bg-white/5"
                          >
                            <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-fk-accent/15 text-fk-ink dark:bg-fk-accent2/20 dark:text-gray-100">
                              <Icon className="h-5 w-5" aria-hidden="true" />
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-fk-ink dark:text-gray-100">{x.title}</div>
                              <div className="mt-1 text-xs leading-relaxed text-fk-muted dark:text-gray-300">{x.desc}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </Card>
              </Reveal>
            </div>
          </Container>
        </section>

        {/* TRUST / PROOF */}
        <Section
          id="proof"
          eyebrow="Proof, not hype"
          title="Built for quoting speed and pricing consistency."
          subtitle="Typical outcomes: faster response times, fewer avoidable errors, and less rework when templates, rules, and human review replace ad-hoc spreadsheets."
        >
          <Reveal>
            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {[
                {
                  title: "Quoting time reduced",
                  desc: "Templates and charge libraries cut repetitive setup.",
                },
                {
                  title: "Fewer pricing errors",
                  desc: "Guardrails plus review reduce missed surcharges and margin drift.",
                },
                {
                  title: "Faster customer response",
                  desc: "Cleaner outputs reduce follow-up loops.",
                },
              ].map((b) => (
                <Card key={b.title} className="p-6">
                  <div className="text-sm font-semibold text-fk-ink dark:text-gray-100">{b.title}</div>
                  <div className="mt-2 text-sm leading-relaxed text-fk-muted dark:text-gray-300">{b.desc}</div>
                </Card>
              ))}
            </div>
          </Reveal>
        </Section>

        {/* PROBLEM */}
        <Section
          id="problem"
          eyebrow="The problem"
          title="Freight quoting breaks down in the same places."
          subtitle="When rates live in too many places, teams lose time, consistency, and margin."
        >
          <Reveal>
            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {[
                {
                  t: "Quotes take too long",
                  d: "Sales spends time assembling charges instead of responding quickly. One missed line item becomes a follow-up loop.",
                },
                {
                  t: "Rates are scattered across spreadsheets",
                  d: "Lane notes, agent rates, and add-ons get copied around, leading to drift and “which sheet is current?” confusion.",
                },
                {
                  t: "Margins leak through inconsistency",
                  d: "Different people apply different markup logic. Small underquotes add up across repeat lanes.",
                },
              ].map((c) => (
                <Card key={c.t} className="p-6">
                  <div className="text-base font-semibold text-fk-ink dark:text-gray-100">{c.t}</div>
                  <div className="mt-3 text-sm leading-relaxed text-fk-muted dark:text-gray-300">{c.d}</div>
                </Card>
              ))}
            </div>
          </Reveal>
        </Section>

        {/* SOLUTION */}
        <Section
          id="solution"
          eyebrow="The solution"
          title="FreightKit centralises your pricing logic into a quoting workflow your team can run daily."
          subtitle="Keep rates, surcharges, templates and customer rules in one place so quotes go out faster and stay consistent."
        >
          <div className="mt-12 grid gap-6 lg:grid-cols-2 lg:items-start">
            <Reveal>
              <Card className="p-6" hoverable={false}>
                <div className="text-sm font-semibold text-fk-ink dark:text-gray-100">What FreightKit gives you</div>
                <ul className="mt-5 space-y-3 text-sm text-fk-muted dark:text-gray-300">
                  {[
                    "Inbox-connected enquiry intake + identification",
                    "Transport provider profiles (airlines, truckers, carriers)",
                    "Pre-filled rate request emails from templates",
                    "Captured responses + route/price comparison",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-xl bg-fk-accent/15 dark:bg-fk-accent2/20">
                        <Check className="h-4 w-4 text-fk-ink dark:text-gray-100" aria-hidden="true" />
                      </span>
                      <span className="leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 flex flex-wrap gap-2">
                  <Chip>Lane</Chip>
                  <Chip>Surcharge</Chip>
                  <Chip>Charge code</Chip>
                  <Chip>Margin floor</Chip>
                  <Chip>Quote-to-win</Chip>
                </div>
              </Card>
            </Reveal>

            <Reveal delay={0.08}>
              <Card className="p-6" hoverable={false}>
                <div className="flex items-center gap-2 text-sm font-semibold text-fk-ink dark:text-gray-100">
                  <Mail className="h-4 w-4 text-black/60 dark:text-white/60" aria-hidden="true" />
                  Cleaner handoffs, fewer follow-ups
                </div>
                <p className="mt-3 text-sm leading-relaxed text-fk-muted dark:text-gray-300">
                  Keep the full thread (enquiry, outbound rate requests, and provider replies) linked to the quote,
                  then compare routes and prices without manual spreadsheets.
                </p>
                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  {[
                    { k: "Providers", v: "Airlines + truckers profiles" },
                    { k: "Requests", v: "Pre-filled emails from templates" },
                    { k: "Responses", v: "Store and compare offers" },
                    { k: "Quoting", v: "Consistent outputs" },
                  ].map((x) => (
                    <div key={x.k} className="rounded-2xl border border-fk-border bg-fk-surface/70 p-4 dark:border-white/10 dark:bg-white/5">
                      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-fk-muted dark:text-gray-300">
                        {x.k}
                      </div>
                      <div className="mt-2 text-sm font-semibold text-fk-ink dark:text-gray-100">{x.v}</div>
                    </div>
                  ))}
                </div>
              </Card>
            </Reveal>
          </div>
        </Section>

        {/* PRODUCT TOUR */}
        <Section
          id="tour"
          eyebrow="Product tour"
          title="A practical flow from first draft to customer-ready quote."
          subtitle="Click through the flow, kept intentionally simple."
        >
          <Reveal>
            <div className="mt-12">
              <div className="flex flex-wrap gap-2">
                {tourSteps.map((s, i) => {
                  const active = i === tourStep;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setTourStep(i)}
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fk-accent focus-visible:ring-offset-2 focus-visible:ring-offset-fk-surface ${active
                          ? "bg-fk-ink text-white dark:bg-white dark:text-gray-950"
                          : "border border-fk-border bg-fk-surface/70 text-fk-ink hover:bg-fk-surface dark:border-white/10 dark:bg-white/5 dark:text-gray-200 dark:hover:bg-white/10"
                        }`}
                      aria-current={active ? "step" : undefined}
                    >
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/15 text-xs dark:bg-black/10">
                        {i + 1}
                      </span>
                      <span className="hidden sm:inline">{s.title}</span>
                      <span className="sm:hidden">Step {i + 1}</span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 grid gap-6 lg:grid-cols-2">
                <Card className="p-6" hoverable={false}>
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={tourSteps[tourStep].id}
                      initial={reduceMotion ? { opacity: 1 } : { opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={reduceMotion ? { opacity: 1 } : { opacity: 0, x: -10 }}
                      transition={{ duration: reduceMotion ? 0 : 0.25, ease: "easeOut" }}
                    >
                      <div className="text-base font-semibold text-fk-ink dark:text-gray-100">
                        {tourSteps[tourStep].title}
                      </div>
                      <ul className="mt-4 space-y-3 text-sm text-fk-muted dark:text-gray-300">
                        {tourSteps[tourStep].bullets.map((b) => (
                          <li key={b} className="flex items-start gap-3">
                            <ChevronRight className="mt-0.5 h-4 w-4 text-black/40 dark:text-white/40" aria-hidden="true" />
                            <span className="leading-relaxed">{b}</span>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  </AnimatePresence>
                </Card>

                <Card className="relative overflow-hidden p-6" hoverable={false}>
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.20),transparent_55%),radial-gradient(circle_at_80%_40%,rgba(96,165,250,0.18),transparent_55%),radial-gradient(circle_at_50%_100%,rgba(11,18,32,0.12),transparent_60%)]"
                  />
                  <div className="relative">
                    <div className="text-sm font-semibold text-fk-ink dark:text-gray-100">What it feels like</div>
                    <div className="mt-2 text-sm text-fk-muted dark:text-gray-300">
                      Less rework. Fewer “what’s included?” emails. A consistent quoting rhythm.
                    </div>
                    <div className="mt-6 grid gap-3 sm:grid-cols-2">
                      {[
                        { k: "Inbox-first", v: "Enquiries don’t get lost" },
                        { k: "Rules", v: "Surcharges + margins stay consistent" },
                        { k: "Templates", v: "Repeat lanes faster" },
                        { k: "History", v: "Reuse what worked" },
                      ].map((x) => (
                        <div key={x.k} className="rounded-2xl border border-fk-border bg-fk-surface/75 p-4 dark:border-white/10 dark:bg-white/5">
                          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-fk-muted dark:text-gray-300">
                            {x.k}
                          </div>
                          <div className="mt-2 text-sm font-semibold text-fk-ink dark:text-gray-100">{x.v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </Reveal>
        </Section>

        {/* FEATURES */}
        <Section
          id="features"
          eyebrow="Features"
          title="Everything you need for quoting, plus the right amount of workflow."
          subtitle="A quoting engine first, with light ops and management add-ons for consistency."
        >
          <Reveal>
            <FeatureGrid groups={featureGroups} />
          </Reveal>
        </Section>

        {/* USE CASES */}
        <Section
          id="use-cases"
          eyebrow="Use cases"
          title="Designed for how freight teams actually work."
          subtitle="Whether it’s sales-led quoting, ops-supported workflows, or an owner-managed team, FreightKit helps keep quotes consistent."
        >
          <Reveal>
            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {[
                {
                  t: "Sales quoting",
                  changes: [
                    "Start from templates instead of building quotes from scratch.",
                    "Apply customer rules without manual re-checking.",
                  ],
                  outcome: "Typical outcome: faster response times and fewer reworks before sending.",
                },
                {
                  t: "Ops supporting sales",
                  changes: [
                    "Keep charge logic and inclusions clear across handoffs.",
                    "Use history to reuse prior decisions on repeat lanes.",
                  ],
                  outcome: "Typical outcome: fewer “what did we include?” follow-ups and cleaner ops handover.",
                },
                {
                  t: "Owner-managed forwarder",
                  changes: [
                    "Set margin presets/floors so pricing stays consistent.",
                    "Standardise output so customers get a clear breakdown.",
                  ],
                  outcome: "Typical outcome: more control with less day-to-day quoting overhead.",
                },
              ].map((u) => (
                <Card key={u.t} className="p-6">
                  <div className="text-base font-semibold text-fk-ink dark:text-gray-100">{u.t}</div>
                  <div className="mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-fk-muted dark:text-gray-300">
                    What changes
                  </div>
                  <ul className="mt-3 space-y-2 text-sm text-fk-muted dark:text-gray-300">
                    {u.changes.map((c) => (
                      <li key={c} className="flex items-start gap-3">
                        <span className="mt-2 inline-block h-2 w-2 rounded-full bg-fk-accent" />
                        <span className="leading-relaxed">{c}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 text-sm font-medium text-fk-ink dark:text-gray-100">{u.outcome}</div>
                </Card>
              ))}
            </div>
          </Reveal>
        </Section>

        {/* PRICING */}
        <Section
          id="pricing"
          eyebrow="Pricing"
          title="Simple pricing that scales with your team."
          subtitle="Use these as placeholders until final tiers are confirmed."
        >
          <Reveal>
            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {[
                {
                  name: "Starter",
                  price: "Talk to us",
                  highlight: false,
                  desc: "For an initial rollout and simple quoting flow.",
                  items: ["Inbox intake", "Templates + charge library", "Customer-ready output"],
                  cta: { type: "demo" },
                },
                {
                  name: "Team",
                  price: "Talk to us",
                  highlight: true,
                  desc: "For sales + ops teams quoting daily.",
                  items: [
                    "Everything in Starter",
                    "Customer-specific rules",
                    "Quote history + search",
                    "Margin presets & floors",
                  ],
                  cta: { type: "demo" },
                },
                {
                  name: "Ops+",
                  price: "Talk to us",
                  highlight: false,
                  desc: "For teams needing stronger controls and governance.",
                  items: ["Everything in Team", "Roles/permissions (as configured)", "Optional approvals"],
                  cta: { type: "demo" },
                },
              ].map((p) => (
                <Card
                  key={p.name}
                  className={`p-6 ${p.highlight ? "ring-2 ring-fk-accent/40" : ""}`}
                  hoverable={false}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-base font-semibold text-fk-ink dark:text-gray-100">{p.name}</div>
                      <div className="mt-1 text-sm text-fk-muted dark:text-gray-300">{p.desc}</div>
                    </div>
                    {p.highlight ? <Chip>Most common</Chip> : null}
                  </div>
                  <div className="mt-6 text-3xl font-semibold tracking-tight text-fk-ink dark:text-gray-100">
                    {p.price}
                  </div>
                  <div className="mt-2 text-sm text-fk-muted dark:text-gray-300">
                    Pricing typically depends on team size and workflow needs.
                  </div>
                  <ul className="mt-6 space-y-3 text-sm text-fk-muted dark:text-gray-300">
                    {p.items.map((it) => (
                      <li key={it} className="flex items-start gap-3">
                        <Check className="mt-0.5 h-4 w-4 text-fk-muted dark:text-gray-300" aria-hidden="true" />
                        <span className="leading-relaxed">{it}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8">
                    <Button
                      as="a"
                      href={CALENDLY_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="w-full justify-center"
                      withArrow
                    >
                      Book a demo
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
            <div className="mx-auto mt-8 max-w-3xl text-center text-sm text-fk-muted dark:text-gray-300">
              Implementation time varies with rate complexity. Many teams start with a small set of lanes and expand templates over time.
            </div>
          </Reveal>
        </Section>

        {/* MODES STRIP — air, sea and road, one desk (live compositions). */}
        <section className="relative py-10 sm:py-12">
          <Container>
            <Reveal>
              <div className="flex flex-col items-center gap-6 rounded-3xl border border-slate-200/70 bg-white/70 px-6 py-8 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/60 sm:flex-row sm:justify-around">
                {[
                  { v: "route", label: "Air", note: "quotes, AWBs, e-docs" },
                  { v: "ship", label: "Sea", note: "LCL / FCL enquiries" },
                  { v: "truck", label: "Road", note: "collection & delivery" },
                ].map((m) => (
                  <div key={m.v} className="flex flex-col items-center text-center">
                    <AnimatedLoader variant={m.v} size={120} label={m.label} />
                    <p className="text-sm font-semibold text-fk-ink dark:text-white">{m.label}</p>
                    <p className="text-xs text-fk-muted dark:text-gray-400">{m.note}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </Container>
        </section>

        {/* FAQ */}
        <Section
          id="faq"
          eyebrow="FAQ"
          title="Answers to common freight quoting questions."
          subtitle="If you have a specific workflow (agents, zones, customer tariffs), book a demo and we’ll walk through it."
        >
          <Reveal>
            <Accordion items={faqItems} />
          </Reveal>
        </Section>

        {/* FINAL CTA */}
        <section id="demo" className="relative py-16 sm:py-20">
          <Container>
            <div className="grid gap-8 lg:grid-cols-2 lg:items-start">
              <Reveal>
                <div>
                  <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-fk-muted dark:text-gray-300">
                    Book a demo
                  </div>
                  <h2 className="text-balance text-3xl font-semibold tracking-tight text-fk-ink dark:text-gray-100 sm:text-4xl">
                    See FreightKit on your enquiry → rates → quote workflow.
                  </h2>
                  <p className="mt-4 max-w-xl text-pretty text-base leading-relaxed text-fk-muted dark:text-gray-300 sm:text-lg">
                    We’ll map inbox intake, provider profiles (airlines/truckers), rate request templates, response capture,
                    and route/price comparison, then show how it rolls into consistent customer-ready quotes.
                  </p>
                  <div className="mt-8 flex flex-wrap gap-2">
                    <Chip>Inbox intake</Chip>
                    <Chip>Provider profiles</Chip>
                    <Chip>Rate templates</Chip>
                    <Chip>Response capture</Chip>
                    <Chip>Compare routes</Chip>
                  </div>
                  <div className="mt-8">
                    <Button
                      as="a"
                      href={CALENDLY_URL}
                      target="_blank"
                      rel="noreferrer"
                      variant="accent"
                      withArrow
                    >
                      Open Calendly
                    </Button>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.08}>
                <Card className="p-4 sm:p-6" hoverable={false}>
                  <div className="text-sm font-semibold text-fk-ink dark:text-gray-100">Schedule a demo</div>
                  <div className="mt-2 text-sm text-fk-muted dark:text-gray-300">
                    Pick a time that works for you. We’ll walk through inbox intake, provider profiles, rate requests, response capture, and comparisons.
                  </div>
                  <div className="mt-6 overflow-hidden rounded-2xl border border-fk-border bg-fk-surface dark:border-white/10 dark:bg-white/5">
                    <CalendlyInline url={CALENDLY_URL} height={720} />
                  </div>
                </Card>
              </Reveal>
            </div>
          </Container>
        </section>

        <Footer />
      </div>
    </MomentumScroll>
    </>
  );
}

